import { useEffect, useState } from "react";
import { Star, ExternalLink, Info } from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal, Kicker } from "@/components/Reveal";
import { FinalCTA } from "@/pages/Home";
import { api } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";

export default function Reviews() {
  const s = useSettings();
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
  }, []);

  return (
    <>
      <SEO
        title="Reviews | Hari Om Trading Co. — Tahliwal, Una"
        description={`Hari Om Trading Co., Tahliwal holds a ${s.google_rating}/5 Google rating from ${s.google_reviews_count} reviews.`}
      />
      <section className="grain bg-[#111111] bg-grid-dark py-16 lg:py-24" data-testid="reviews-hero">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <Reveal>
            <Kicker dark>Reviews</Kicker>
            <div className="mt-4 flex flex-wrap items-end gap-8">
              <h1 className="font-heading text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">
                Google Reviews
              </h1>
              <div className="pb-2" data-testid="reviews-aggregate">
                <p className="font-heading text-6xl font-bold text-[#F5B700]">{s.google_rating}</p>
                <div className="mt-1 flex gap-0.5" aria-label={`${s.google_rating} out of 5 stars`}>
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} size={16} className="fill-[#F5B700] text-[#F5B700]" aria-hidden="true" />
                  ))}
                </div>
                <p className="mt-1.5 text-xs font-medium uppercase tracking-widest text-[#b8bec8]">
                  {s.google_reviews_count} Google reviews
                </p>
              </div>
            </div>
            <a
              href={s.google_reviews_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="view-google-reviews"
              className="mt-8 inline-flex min-h-[48px] items-center gap-2 bg-[#F5B700] px-6 py-3 font-heading text-[15px] font-semibold text-[#111111] hover:bg-[#D99C00]"
            >
              View Google Reviews <ExternalLink size={15} aria-hidden="true" />
            </a>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#F6F7F8] py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <Reveal>
            <div className="mb-8 flex items-start gap-3 border border-[#D9DDE3] bg-white p-5" data-testid="reviews-notice">
              <Info size={18} className="mt-0.5 shrink-0 text-[#F5B700]" aria-hidden="true" />
              <p className="text-sm leading-relaxed text-[#667085]">
                The cards below are <strong className="text-[#161616]">placeholders</strong>. They will be replaced
                with actual verified Google reviews — no invented testimonials are shown.
              </p>
            </div>
          </Reveal>
          <div className="grid gap-5 md:grid-cols-3">
            {reviews.map((r, i) => (
              <Reveal key={r.id} delay={i * 0.08}>
                <div className="flex h-full flex-col border border-dashed border-[#b9bfc9] bg-white p-7" data-testid={`review-card-${i}`}>
                  <p className="font-mono text-xs font-semibold text-[#98a2b3]">{r.author}</p>
                  <p className="mt-4 flex-1 text-sm leading-relaxed text-[#667085]">{r.text}</p>
                  <p className="mt-5 text-[11px] font-semibold uppercase tracking-widest text-[#98a2b3]">
                    Placeholder — awaiting verified review
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <FinalCTA />
    </>
  );
}
